import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CalendarPage.css';
import { VIEW_MODES } from './utils/calendarConstants';
import { generateMockEntries } from './utils/calendarUtils';
import WeekView from './views/WeekView';
import MonthView from './views/MonthView';
import QuarterView from './views/QuarterView';
import YearView from './views/YearView';
import CalendarTooltip from './components/CalendarTooltip';
import CalendarPopover from './components/CalendarPopover';
import NavigationArrows from './components/NavigationArrows';

function CalendarPage() {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const popoverRef = useRef(null);
  const [viewMode, setViewMode] = useState('month');
  const [entriesByDate, setEntriesByDate] = useState(() => generateMockEntries());
  const [tooltip, setTooltip] = useState(null);
  const [popover, setPopover] = useState(null);
  const [draftEntry, setDraftEntry] = useState('');
  const [dateOffset, setDateOffset] = useState(0);

  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        popover &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setPopover(null);
      }
    };

    document.addEventListener('click', handleClickAway);
    return () => document.removeEventListener('click', handleClickAway);
  }, [popover]);

  useEffect(() => {
    const handleResize = () => {
      setTooltip(null);
      setPopover(null);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCellHover = (event, cell) => {
    event.stopPropagation();
    const cellRect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      label: cell.isoDate ? cell.isoDate : `${cell.dayName} ${cell.dayNumber}`,
      top: cellRect.top - 32,
      left: cellRect.left + cellRect.width / 2
    });
  };

  const handleCellLeave = () => {
    setTooltip(null);
  };

  const handleCellClick = (event, cell) => {
    event.stopPropagation();
    if (!cell.isoDate) return;

    const cellRect = event.currentTarget.getBoundingClientRect();
    setPopover({
      date: cell.isoDate,
      top: cellRect.top - 12,
      left: cellRect.left + cellRect.width / 2,
      clickedHour: null
    });
    setDraftEntry('');
  };


  const handleAddEntry = (startTime = null, endTime = null) => {
    const targetDate = popover?.date;
    if (!targetDate || !draftEntry.trim()) return;

    setEntriesByDate((prev) => {
      const next = { ...prev };
      const current = next[targetDate] ? [...next[targetDate]] : [];
      const newEntry = {
        id: `${targetDate}-${Date.now()}`,
        text: draftEntry.trim(),
        done: false
      };
      if (viewMode === 'week' && startTime && endTime) {
        newEntry.startTime = startTime;
        newEntry.endTime = endTime;
      }
      current.push(newEntry);
      next[targetDate] = current;
      return next;
    });

    setDraftEntry('');
  };
  
  const handleAddEntryForDate = (date, text, startTime = null, endTime = null) => {
    if (!date || !text.trim()) return;

    setEntriesByDate((prev) => {
      const next = { ...prev };
      const current = next[date] ? [...next[date]] : [];
      const newEntry = {
        id: `${date}-${Date.now()}`,
        text: text.trim(),
        done: false
      };
      if (viewMode === 'week' && startTime && endTime) {
        newEntry.startTime = startTime;
        newEntry.endTime = endTime;
      }
      current.push(newEntry);
      next[date] = current;
      return next;
    });
  };

  const handleToggleEntry = (date, entryId) => {
    setEntriesByDate((prev) => {
      const next = { ...prev };
      next[date] = next[date].map((entry) =>
        entry.id === entryId ? { ...entry, done: !entry.done } : entry
      );
      return next;
    });
  };

  const handleDeleteEntry = (date, entryId) => {
    setEntriesByDate((prev) => {
      const next = { ...prev };
      const filtered = next[date].filter((entry) => entry.id !== entryId);
      if (filtered.length > 0) {
        next[date] = filtered;
      } else {
        delete next[date];
      }
      return next;
    });
  };

  const handleUpdateEntry = (date, entryId, newText, newStartTime, newEndTime) => {
    setEntriesByDate((prev) => {
      const next = { ...prev };
      next[date] = next[date].map((entry) => {
        if (entry.id === entryId) {
          const updated = { ...entry, text: newText };
          if (viewMode === 'week') {
            updated.startTime = newStartTime || null;
            updated.endTime = newEndTime || null;
          }
          return updated;
        }
        return entry;
      });
      return next;
    });
  };

  const handleNavigate = (direction) => {
    if (direction === 'back') {
      setDateOffset(prev => prev - 1);
    } else if (direction === 'forward') {
      setDateOffset(prev => prev + 1);
    }
    setPopover(null);
    setTooltip(null);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setDateOffset(0);
    setPopover(null);
  };

  const renderView = () => {
    const commonProps = {
      entries: entriesByDate,
      offset: dateOffset,
      onCellHover: handleCellHover,
      onCellLeave: handleCellLeave,
      onCellClick: handleCellClick,
      selectedDate: popover?.date
    };

    switch (viewMode) {
      case 'week':
        return <WeekView 
          {...commonProps} 
          onToggleEntry={handleToggleEntry}
          onUpdateEntry={handleUpdateEntry}
          onDeleteEntry={handleDeleteEntry}
          onAddEntry={handleAddEntryForDate}
        />;
      case 'month':
        return <MonthView {...commonProps} />;
      case 'quarter':
        return <QuarterView {...commonProps} />;
      case 'year':
        return <YearView entries={entriesByDate} onCellHover={handleCellHover} onCellLeave={handleCellLeave} onCellClick={handleCellClick} selectedDate={popover?.date} />;
      default:
        return <MonthView {...commonProps} />;
    }
  };

  return (
    <div className="calendar-page">
      <header className="calendar-header">
        <div>
          <p className="calendar-kicker">Planning</p>
          <h1>Calendar</h1>
        </div>
        <div className="calendar-header-actions">
          <div className="calendar-view-toggle">
            {VIEW_MODES.map((mode) => (
              <button
                key={mode}
                className={`view-pill ${viewMode === mode ? 'active' : ''}`}
                onClick={() => handleViewModeChange(mode)}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <button
            className="calendar-back-button"
            onClick={() => navigate('/')}
          >
            Back to Dashboard
          </button>
        </div>
      </header>

      <section className="calendar-main">
        {viewMode !== 'week' && (
          <div className="calendar-legend">
            <span className="legend-label">Pocket density</span>
            <div className="legend-items">
              <span className="legend-item">
                <span className="legend-swatch intensity-0" /> Empty
              </span>
              <span className="legend-item">
                <span className="legend-swatch intensity-1" /> 1 pocket
              </span>
              <span className="legend-item">
                <span className="legend-swatch intensity-2" /> 2 pockets
              </span>
              <span className="legend-item">
                <span className="legend-swatch intensity-3" /> 3+ pockets
              </span>
            </div>
          </div>
        )}

        {viewMode === 'week' && (
          <div className="calendar-legend">
            <NavigationArrows
              onNavigate={handleNavigate}
              canGoBack={true}
              canGoForward={true}
              viewMode={viewMode}
            />
          </div>
        )}

        <div className="calendar-heatmap" ref={gridRef}>
          {viewMode !== 'week' && (
            <NavigationArrows
              onNavigate={handleNavigate}
              canGoBack={true}
              canGoForward={true}
              viewMode={viewMode}
            />
          )}
          {renderView()}
        </div>
      </section>

      <CalendarTooltip tooltip={tooltip} />
      <CalendarPopover
        popover={popover}
        entries={entriesByDate}
        draftEntry={draftEntry}
        onDraftChange={setDraftEntry}
        onAddEntry={handleAddEntry}
        onToggleEntry={handleToggleEntry}
        onDeleteEntry={handleDeleteEntry}
        onUpdateEntry={handleUpdateEntry}
        onClose={() => setPopover(null)}
        popoverRef={popoverRef}
        viewMode={viewMode}
      />
    </div>
  );
}

export default CalendarPage;
